/**
 * PillarTooltip — First-visit contextual education card (Spec 163).
 *
 * Shows a brief card below SecondaryNav when a user first navigates to
 * each pillar. Dismissed per-pillar via "Got it" or globally via "Skip all".
 * State tracked in localStorage — no backend/database dependency.
 *
 * First-visit contextual tooltip — shown on each pillar's first visit.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { getActivePillar, getPillarConfig, type PillarId } from './navConfig';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'helm_pillar_tooltips_seen';
const ALL_PILLARS: PillarId[] = ['cultivate', 'connect', 'command', 'chronicle'];

function getSeenPillars(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function isPillarSeen(pillarId: PillarId): boolean {
  const seen = getSeenPillars();
  return seen.includes('all') || seen.includes(pillarId);
}

function markSeen(pillarId: PillarId | 'all'): void {
  const seen = getSeenPillars();
  if (!seen.includes(pillarId)) {
    seen.push(pillarId);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    } catch {
      // Silent fail — tooltip may re-appear next session, which is acceptable
    }
  }
}

interface PillarTooltipProps {
  /** Override which pillar to show tooltip for (e.g. Helm dashboard with default pillar) */
  overridePillarId?: PillarId | null;
}

const PillarTooltip: React.FC<PillarTooltipProps> = ({ overridePillarId }) => {
  const { pathname } = useLocation();
  const activePillarId = overridePillarId || getActivePillar(pathname);
  const [dismissed, setDismissed] = useState(false);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reset dismissed state when pillar changes
  useEffect(() => {
    setDismissed(false);
  }, [activePillarId]);

  // Escape key dismisses tooltip
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activePillarId && !isPillarSeen(activePillarId)) {
        markSeen(activePillarId);
        setDismissed(true);
      }
    },
    [activePillarId],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Gate: must be on a non-admin pillar
  if (!activePillarId || activePillarId === 'admin') return null;

  // Gate: already seen or dismissed this session
  if (dismissed || isPillarSeen(activePillarId)) return null;

  const pillar = getPillarConfig(activePillarId);
  if (!pillar?.tooltip) return null;

  const handleGotIt = () => {
    markSeen(activePillarId);
    setDismissed(true);
  };

  const handleSkipAll = () => {
    markSeen('all');
    setDismissed(true);
  };

  // Count remaining unseen pillars for "Skip all" context
  const unseenCount = ALL_PILLARS.filter((p) => !isPillarSeen(p)).length;

  return (
    <div
      className={`pillar-tooltip-wrapper w-full flex justify-center px-3 md:px-6 ${
        prefersReducedMotion ? '' : 'animate-pillar-tooltip-slide-down'
      }`}
      data-testid="pillar-tooltip"
      role="status"
      aria-label={`About the ${pillar.label} section`}
    >
      <Card tone="elevated" variant="bordered" borderColor="medium" radius="lg" padding="md" className="pillar-tooltip-card w-full max-w-[400px] my-3 shadow-sm">
        <p className="pillar-tooltip-title m-0 mb-1 text-base font-semibold text-[var(--theme-text-primary)]">
          {pillar.tooltip.title}
        </p>
        <p className="pillar-tooltip-description m-0 mb-3 text-sm text-text-secondary">
          {pillar.tooltip.description}
        </p>
        <div className="pillar-tooltip-actions flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleGotIt}
          >
            Got it
          </Button>
          {unseenCount > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkipAll}
            >
              Skip all
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PillarTooltip;
